import { memo, useMemo } from 'react';
import { getXTGrid } from '@4cm/core/tactics/xtGrid';

/**
 * Expected Threat overlay for the 2D tactics board. Static reference
 * field — every cell of the 12×16 grid renders as one SVG `<rect>`
 * tinted by the cool→hot ramp the heatmap layer uses, scaled by the
 * cell's published xT value.
 *
 * Coordinate mapping mirrors the rest of the 2D board (`SVG-x =
 * -world.z`, `SVG-y = world.x`).
 */

interface RampStop {
  t: number;
  rgb: [number, number, number];
}
const RAMP: RampStop[] = [
  { t: 0.0, rgb: [56, 130, 232] },   // cool blue
  { t: 0.4, rgb: [74, 222, 128] },   // green
  { t: 0.7, rgb: [251, 191, 36] },   // yellow / amber
  { t: 1.0, rgb: [239, 68, 68] },    // red
];

function rampColor(t: number): [number, number, number] {
  if (!(t > 0)) return RAMP[0]!.rgb;
  if (t >= 1) return RAMP[RAMP.length - 1]!.rgb;
  for (let i = 1; i < RAMP.length; i++) {
    const upper = RAMP[i]!;
    if (t <= upper.t) {
      const lower = RAMP[i - 1]!;
      const span = upper.t - lower.t;
      const f = span > 0 ? (t - lower.t) / span : 0;
      return [
        Math.round(lower.rgb[0] + (upper.rgb[0] - lower.rgb[0]) * f),
        Math.round(lower.rgb[1] + (upper.rgb[1] - lower.rgb[1]) * f),
        Math.round(lower.rgb[2] + (upper.rgb[2] - lower.rgb[2]) * f),
      ];
    }
  }
  return RAMP[RAMP.length - 1]!.rgb;
}

/** xT values peak around 0.28 — normalise into [0,1] for the ramp by
 *  dividing by the max we expect to see. Coach can dial the master
 *  opacity slider down for subtle. */
const XT_NORMALISER = 0.28;

export interface XTLayer2DProps {
  /** Caller-controlled opacity in [0, 1]. */
  opacity: number;
}

interface RenderCell {
  key: string;
  x: number;
  y: number;
  w: number;
  h: number;
  fill: string;
}

function XTLayer2DInner({ opacity }: XTLayer2DProps) {
  const cells = useMemo<RenderCell[]>(() => {
    const grid = getXTGrid();
    const out: RenderCell[] = [];
    const { nRows, nCols, cellLengthM, cellWidthM, halfL, halfW, values } = grid;
    for (let r = 0; r < nRows; r++) {
      // World x range for this row.
      const worldXMin = -halfW + r * cellWidthM;
      // SVG-y mirrors world.x — same value.
      const y = worldXMin;
      for (let c = 0; c < nCols; c++) {
        const v = values[r * nCols + c]!;
        const norm = Math.min(1, v / XT_NORMALISER);
        if (norm < 0.02) continue;
        // World z range for this col.
        const worldZMin = -halfL + c * cellLengthM;
        // SVG-x = -world.z; use the cell's max-z corner so the rect
        // grows from svgX → svgX + cellLengthM in SVG space.
        const x = -(worldZMin + cellLengthM);
        const [rr, gg, bb] = rampColor(norm);
        const alpha = norm * opacity;
        out.push({
          key: `${r}-${c}`,
          x,
          y,
          w: cellLengthM,
          h: cellWidthM,
          fill: `rgba(${rr}, ${gg}, ${bb}, ${alpha})`,
        });
      }
    }
    return out;
  }, [opacity]);

  if (cells.length === 0) return null;

  return (
    <g
      className="t2d-xt-layer"
      data-testid="t2d-xt-layer"
      pointerEvents="none"
      clipPath="url(#t2d-pitch-clip)"
    >
      {cells.map((cell) => (
        <rect
          key={cell.key}
          x={cell.x}
          y={cell.y}
          width={cell.w}
          height={cell.h}
          fill={cell.fill}
          data-testid={`t2d-xt-cell-${cell.key}`}
        />
      ))}
    </g>
  );
}

export const XTLayer2D = memo(XTLayer2DInner);
